import {
  Building2, Brain, Globe, Briefcase, Mail, FileText, DollarSign, Layers, X, Plus,
} from "lucide-react"
import { cn } from "@/utils/cn"
import { modeloOptions, faturamentoOptions } from "./constants"
import { LeadSourceSelect } from "@/components/shared/LeadSourceSelect"
import { fieldClass, FormField } from "./formField"
import type { BdrLeadFormProps } from "./types"
import type { useBdrLeadFormCompany } from "./useBdrLeadFormCompany"

type CompanyHook = ReturnType<typeof useBdrLeadFormCompany>

export function BdrLeadFormExtended(props: BdrLeadFormProps & { company: CompanyHook }) {
  const { newLead, setNewLead, backendErrors, company } = props
  const { updateField, servicoInput, setServicoInput, addServico, removeServico, handleServicoKeyDown } = company

  return (
    <>
      {/* ── Coluna Esquerda: Dados Complementares ─────────────────────────── */}
      <section className="space-y-4 bg-white/[0.01] border border-white/[0.04] p-5 rounded-2xl">
        <div className="flex items-center gap-2 mb-2">
          <Building2 size={15} className="text-accent" />
          <h3 className="text-text-primary font-bold text-xs uppercase tracking-widest">
            Dados Complementares
          </h3>
        </div>

        <FormField label="Site" error={backendErrors.site}>
          <div className="relative">
            <input
              type="text"
              value={newLead.site || ''}
              onChange={e => updateField('site', e.target.value)}
              placeholder="https://..."
              className={fieldClass()}
            />
            <Globe size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        </FormField>

        <FormField label="Cargo" error={backendErrors.cargo}>
          <div className="relative">
            <input
              type="text"
              value={newLead.cargo || ''}
              onChange={e => updateField('cargo', e.target.value)}
              placeholder="Ex: Diretor de Marketing"
              className={fieldClass()}
            />
            <Briefcase size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        </FormField>

        <FormField label="E-mail" error={backendErrors.email}>
          <div className="relative">
            <input
              type="email"
              value={newLead.email || ''}
              onChange={e => updateField('email', e.target.value)}
              placeholder="contato@empresa.com"
              className={fieldClass()}
            />
            <Mail size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        </FormField>

        <FormField label="CNPJ" error={backendErrors.cnpj}>
          <div className="relative">
            <input
              type="text"
              value={newLead.cnpj || ''}
              onChange={e => updateField('cnpj', e.target.value)}
              placeholder="00.000.000/0000-00"
              className={fieldClass()}
            />
            <FileText size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          {/* Faturamento Dropdown aligned with Old CRM */}
          <FormField label="Faturamento" error={backendErrors.faturamento}>
            <select
              value={newLead.faturamento || ''}
              onChange={e => updateField('faturamento', e.target.value)}
              className={fieldClass()}
            >
              <option value="">Selecione...</option>
              {faturamentoOptions.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Modelo" error={backendErrors.modelo}>
            <select
              value={newLead.modelo || ''}
              onChange={e => updateField('modelo', e.target.value)}
              className={fieldClass()}
            >
              <option value="">Selecione...</option>
              {modeloOptions.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </FormField>
        </div>
      </section>

      {/* ── Coluna Direita: Qualificação & Inteligência ─────────────────────── */}
      <section className="space-y-4 bg-white/[0.01] border border-white/[0.04] p-5 rounded-2xl">
        <div className="flex items-center gap-2 mb-2">
          <Brain size={15} className="text-accent" />
          <h3 className="text-text-primary font-bold text-xs uppercase tracking-widest">
            Qualificação & Inteligência
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Segmento" error={backendErrors.segmento}>
            <input
              type="text"
              value={newLead.segmento || ''}
              onChange={e => updateField('segmento', e.target.value)}
              placeholder="Ex: E-commerce, SaaS..."
              className={fieldClass()}
            />
          </FormField>

          <FormField label="Plataforma" error={backendErrors.plataforma}>
            <input
              type="text"
              value={newLead.plataforma || ''}
              onChange={e => updateField('plataforma', e.target.value)}
              placeholder="Ex: Shopify, VTEX..."
              className={fieldClass()}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Origem" error={backendErrors.origem}>
            <LeadSourceSelect
              label=""
              value={newLead.origem || ''}
              onChange={(v) => updateField('origem', v)}
              showHint={false}
              selectClassName={fieldClass(backendErrors.origem)}
              error={backendErrors.origem}
            />
          </FormField>

          {/* Traffic input text aligned with Old CRM */}
          <FormField label="Tráfego (Acessos Orgânicos)" error={backendErrors.trafego}>
            <input
              type="text"
              value={newLead.trafego || ''}
              onChange={e => updateField('trafego', e.target.value)}
              placeholder="Ex: 50k/mês, Pago..."
              className={fieldClass()}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <FormField label="Budget Estimado" error={backendErrors.budget}>
            <div className="relative">
              <input
                type="text"
                value={newLead.budget || ''}
                onChange={e => updateField('budget', e.target.value)}
                placeholder="Ex: 5k-10k..."
                className={fieldClass()}
              />
              <DollarSign size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
            </div>
          </FormField>

          <div className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-xl h-[44px]">
            <input
              type="checkbox"
              id="decisor"
              checked={!!newLead.decisor}
              onChange={e => setNewLead((prev: any) => ({ ...prev, decisor: e.target.checked }))}
              className="w-4 h-4 rounded border-white/20 bg-white/5 text-accent focus:ring-accent"
            />
            <label htmlFor="decisor" className="text-xs text-text-secondary font-bold cursor-pointer select-none">
              Contato é o Decisor?
            </label>
          </div>
        </div>

        <FormField label="Problema Identificado" error={backendErrors.problema}>
          <textarea
            value={newLead.problema || ''}
            onChange={e => updateField('problema', e.target.value)}
            placeholder="Qual dor ou desafio principal do lead?"
            rows={2}
            className={cn(fieldClass(), 'resize-none')}
          />
        </FormField>

        <FormField label="Dores Identificadas" error={backendErrors.dores}>
          <textarea
            value={newLead.dores || ''}
            onChange={e => updateField('dores', e.target.value)}
            placeholder="Liste as principais dores para o Closer..."
            rows={2}
            className={cn(fieldClass(), 'resize-none')}
          />
        </FormField>

        <FormField label="Observações" error={backendErrors.observacoes}>
          <textarea
            value={newLead.observacoes || ''}
            onChange={e => updateField('observacoes', e.target.value)}
            placeholder="Notas adicionais sobre o lead..."
            rows={2}
            className={cn(fieldClass(), 'resize-none')}
          />
        </FormField>
      </section>

      {/* ── Seção Inferior: Serviços (full width) ─────────────────────────── */}
      <section className="lg:col-span-2 space-y-4 bg-white/[0.01] border border-white/[0.04] p-5 rounded-2xl">
        <div className="flex items-center gap-2 mb-1">
          <Layers size={15} className="text-accent" />
          <h3 className="text-text-primary font-bold text-xs uppercase tracking-widest">
            Serviços de Interesse
          </h3>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={servicoInput}
            onChange={e => setServicoInput(e.target.value)}
            onKeyDown={handleServicoKeyDown}
            placeholder="Digite um serviço e pressione Enter"
            className={cn(fieldClass(), 'flex-1')}
          />
          <button
            onClick={addServico}
            disabled={!servicoInput.trim()}
            className={cn(
              'px-4 rounded-lg flex items-center justify-center transition-all min-w-[44px] min-h-[44px]',
              servicoInput.trim()
                ? 'bg-accent text-white hover:brightness-110'
                : 'bg-white/[0.04] text-text-muted cursor-not-allowed'
            )}
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Tags */}
        {newLead.servicos && newLead.servicos.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {newLead.servicos.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold border border-accent/20"
              >
                {tag}
                <button
                  onClick={() => removeServico(tag)}
                  className="hover:text-white transition-colors"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </section>
    </>
  )
}
